import { JwtHelperService } from '@auth0/angular-jwt';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { from, map, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../environment/environment';
import { AuthService } from '../../../auth/auth.service';
import { TokenStateService } from '../../../token-state/token-state.service';

/** Org settings API requires a logged-in session; guests use empty defaults (no 401 / refresh loop). */
async function canFetchOrgSettings(service: ComponentDataSourceService): Promise<boolean> {
  const authService = service.injector.get(AuthService);
  const tokenState = service.injector.get(TokenStateService);
  const jwtHelper = service.injector.get(JwtHelperService);
  const token = await tokenState.getToken();
  if (!token || jwtHelper.isTokenExpired(token, 30)) {
    return false;
  }
  return authService.isAuthenticated;
}

/**
 * Data source for organization settings.
 * Params: organizationId (required), optional key for single setting.
 * - With key: GET /organizations/{id}/settings/{key} -> returns { key, value }.
 * - Without key: GET /organizations/{id}/settings -> returns { data }.
 */
export function org_settings(service: ComponentDataSourceService): DataSourceFunction {
  return {
    name: 'org_settings',
    description: 'Get organization settings (all or one by key)',
    execute: (params?: LoadDataParams): Observable<{ data?: Record<string, unknown>; key?: string; value?: unknown }> => {
      const orgId = params?.organizationId ?? params?.queryParams?.['organizationId'] ?? params?.filters?.find((f: { column: string }) => f.column === 'organizationId')?.value;
      const key = params?.key ?? params?.queryParams?.['key'] ?? params?.filters?.find((f: { column: string }) => f.column === 'key')?.value;

      if (orgId == null) {
        return of({ data: {} });
      }

      const id = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);

      return from(canFetchOrgSettings(service)).pipe(
        switchMap((allowed) => {
          if (!allowed) {
            if (key != null && key !== '') {
              return of({ key: String(key), value: undefined });
            }
            return of({ data: {} });
          }
          if (key != null && key !== '') {
            return service.http
              .get<{ key: string; value: unknown }>(
                `${environment.apiUrl}/organizations/${id}/settings/${encodeURIComponent(String(key))}`
              )
              .pipe(map((res) => ({ key: res.key, value: res.value })));
          }
          return service.http
            .get<{ data: Record<string, unknown> }>(`${environment.apiUrl}/organizations/${id}/settings`)
            .pipe(map((res) => ({ data: res.data ?? {} })));
        })
      );
    }
  };
}
